import React, { useEffect, useState } from "react";
import MyCustomSelect from "../../common/elements/myCustomSelect";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { postVisitTracking } from "../../api/VisitTrackingApi";
import { openNotification } from "../../common/elements/myNotification";

const dateFormat = ["YYYY-MM-DD"];

const VisitTrackingForm = ({
  visitTypeOptions,
  statusOptions,
  schedulingDetailOptions,
  editRecord,
  callback,
  provList,
  performanceYear,
  patientData,
  fromHome = false,
  selectedMBIs,
}) => {
  const [formData, setFormData] = useState(
    editRecord?.guid
      ? {
          ...editRecord,
          performyr: performanceYear,
          modifiedBy: sessionStorage.getItem("userName"),
          visitStatusScheduledDate:
            dayjs(editRecord?.visitStatusScheduledDate).format(
              "YYYY-MM-DD HH:mm A"
            ) === "1900-01-01 00:00 AM"
              ? ""
              : editRecord?.visitStatusScheduledDate,
        }
      : {
          ...editRecord,
          providerType: "CCMD Provider",
          claimType: "ChartPath",
          visitLocation: "In Person",
          performyr: performanceYear,
        }
  );

  const [visitType, setVisitType] = useState();
  const [visitStatus, setVisitStatus] = useState();
  const [schedulingDetails, setSchedulingDetails] = useState();
  const [renderingProvider, setRenderingProvider] = useState();
  const [pageError, setPageError] = useState({});
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    if (editRecord?.guid) {
      let visitTypeTemp = [];
      if (editRecord?.visitTypeValue) {
        visitTypeTemp = visitTypeOptions?.filter((item) => {
          return item.value === editRecord?.visitTypeValue;
        });
        setVisitType(visitTypeTemp);
      }

      let visitStatusTemp = [];
      if (editRecord?.visitStatusValue) {
        visitStatusTemp = statusOptions?.filter((item) => {
          return item.value === editRecord?.visitStatusValue;
        });
        setVisitStatus(visitStatusTemp);
      }

      let schedulingDetailsTemp = [];
      if (editRecord?.schedulingDetailsValue) {
        schedulingDetailsTemp = schedulingDetailOptions?.filter((item) => {
          return item.value === editRecord?.schedulingDetailsValue;
        });
        setSchedulingDetails(schedulingDetailsTemp);
      }

      let renderingProviderTemp = [];
      if (editRecord?.renderProvider) {
        renderingProviderTemp = provList?.filter((item) => {
          return item.value === editRecord?.renderProvider;
        });
        setRenderingProvider(renderingProviderTemp);
      }

      if (editRecord?.dateTimeCreated === "NaT") {
        setFormData({
          ...formData,
          dateTimeCreated: "",
        });
      }
    }
  }, [editRecord]);

  useEffect(() => {
    if (!editRecord?.visitStatusValue) {
      const defaultVisitStatus = statusOptions?.find(
        (item) => item.label === "To Be Scheduled"
      );

      if (defaultVisitStatus) {
        setVisitStatus([defaultVisitStatus]);
        setFormData({
          ...formData,
          visitStatusLabel: defaultVisitStatus.label,
          visitStatusValue: defaultVisitStatus.value,
        });
      }
    }
  }, [editRecord, statusOptions]);

  const handleInputChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    setPageError({ ...pageError, [name]: value ? "" : "Required" });
  };

  const onVisitTypeChange = (e) => {
    setVisitType(e);
    setFormData({
      ...formData,
      visitTypeLabel: e ? e.label : "",
      visitTypeValue: e ? e.value : "",
    });
    setPageError({ ...pageError, visitTypeValue: e ? "" : "Required" });
    if (e?.value !== "Other") {
      setFormData({
        ...formData,
        visitTypeLabel: e ? e.label : "",
        visitTypeValue: e ? e.value : "",
        visitTypeOther: "",
      });
    }
  };

  const onVisitStatusChange = (e) => {
    setVisitStatus(e);
    setFormData({
      ...formData,
      visitStatusLabel: e ? e.label : "",
      visitStatusValue: e ? e.value : "",
    });
    setPageError({ ...pageError, visitStatusValue: e ? "" : "Required" });
    if (e?.value !== "scheduled") {
      setFormData({
        ...formData,
        visitStatusLabel: e ? e.label : "",
        visitStatusValue: e ? e.value : "",
        visitStatusScheduledDate: "",
      });
    }
    if (e?.value !== "completed") {
      setFormData({
        ...formData,
        visitStatusLabel: e ? e.label : "",
        visitStatusValue: e ? e.value : "",
        visitStatusScheduledDate: "",
      });
    }
    if (e?.value !== "changeOfPCP") {
      setFormData({
        ...formData,
        visitStatusLabel: e ? e.label : "",
        visitStatusValue: e ? e.value : "",
        visitStatusScheduledDate: "",
      });
    }
    if (e?.value !== "dealigned") {
      setFormData({
        ...formData,
        visitStatusLabel: e ? e.label : "",
        visitStatusValue: e ? e.value : "",
        visitStatusScheduledDate: "",
      });
    }
    if (e?.value !== "termedProvider") {
      setFormData({
        ...formData,
        visitStatusLabel: e ? e.label : "",
        visitStatusValue: e ? e.value : "",
        visitStatusScheduledDate: "",
      });
    }
    if (e?.value !== "refused") {
      setFormData({
        ...formData,
        visitStatusLabel: e ? e.label : "",
        visitStatusValue: e ? e.value : "",
        visitStatusScheduledDate: "",
      });
    }
  };

  const onSchedulingDetailsChange = (e) => {
    setSchedulingDetails(e);
    setFormData({
      ...formData,
      schedulingDetailsLabel: e ? e.label : "",
      schedulingDetailsValue: e ? e.value : "",
    });
  };

  const onRenderingProviderChange = (e) => {
    setRenderingProvider(e);
    setFormData({
      ...formData,
      renderProvider: e ? e.value : "",
    });
    // setPageError({ ...pageError, renderProvider: e ? "" : "Required" });
  };

  const onVisitStatusDateChange = (date, dateString) => {
    const isTermedProvider = formData?.visitStatusValue === "termedProvider";

    setFormData({
      ...formData,
      visitStatusScheduledDate: dateString ? dateString : "",
    });

    // Only set the error if the visitStatus is not 'termedProvider'
    setPageError({
      ...pageError,
      visitStatusScheduledDate: isTermedProvider
        ? ""
        : dateString
        ? ""
        : "Required",
    });
  };

  const onSchedulingDetailDateChange = (date, dateString) => {
    setFormData({
      ...formData,
      schedulingDetailDate: dateString ? dateString : "",
    });
  };

  const providerTypeChange = (e) => {
    setFormData({
      ...formData,
      providerType: e ? e.currentTarget.value : "",
    });
  };

  const claimTypeChange = (e) => {
    setFormData({
      ...formData,
      claimType: e ? e.currentTarget.value : "",
    });
  };

  const visitLocationChange = (e) => {
    setFormData({
      ...formData,
      visitLocation: e ? e.currentTarget.value : "",
    });
  };

  const onSubmit = (e) => {
    console.log("formData", formData);
    console.log("pageError", pageError);

    e.preventDefault();
    setPageError({});

    let pageErrorTemp = {};

    if (!formData?.visitTypeValue) {
      pageErrorTemp.visitTypeValue = "Required";
    }

    if (formData?.visitTypeValue === "other" && !formData?.visitTypeOther) {
      pageErrorTemp.visitTypeOther = "Required";
    }

    if (!formData?.visitStatusValue) {
      pageErrorTemp.visitStatusValue = "Required";
    }

    const requiredStatuses = [
      "scheduled",
      "completed",
      "changeOfPCP",
      "dealigned",
      "refused",
    ];

    const status = formData?.visitStatusValue;
    if (
      requiredStatuses.includes(status) &&
      !formData?.visitStatusScheduledDate
    ) {
      pageErrorTemp.visitStatusScheduledDate = "Required";
    }

    if (Object.keys(pageErrorTemp).length > 0) {
      setPageError(pageErrorTemp);
      return;
    }

    onSave(e);
  };

  const onSave = async (e) => {
    setDisable(true);
    try {
      if (fromHome && selectedMBIs?.length > 0) {
        let addedCount = 0;

        for (const mbi of selectedMBIs) {
          const formDataWithMBI = { ...formData, mbi }; // Assign MBI to formData
          const response = await postVisitTracking(formDataWithMBI);
          if (response?.status === 200) {
            addedCount++;
          }
        }

        openNotification(
          "success",
          "Added",
          `Bulk Updates to ${addedCount} Patients Done Successfully!`
        );
        callback(false);
      } else {
        const response = await postVisitTracking(formData);
        if (response?.status === 200 && !formData?.guid) {
          openNotification(
            "success",
            "Added",
            "Visit Tracking Added Successfully!"
          );
        } else if (formData?.guid) {
          openNotification(
            "success",
            "Updated",
            "Visit Tracking Updated Successfully!"
          );
        } else {
          openNotification("error", "Error", "Visit Tracking Failed to Add!");
        }
        setFormData(editRecord);
        callback(response);
      }
    } catch (e) {
      console.error("Error saving visit tracking:", e);
    }
    setDisable(false);
  };

  const disabledDate = (current) => {
    const deceasedDate = patientData?.deceasedDate
      ? dayjs(patientData.deceasedDate).endOf("day")
      : null;
    if (!performanceYear) return false;

    const startOfYear = dayjs(`${performanceYear}-01-01`).startOf("day");
    const endOfYear =
      deceasedDate || dayjs(`${performanceYear}-12-31`).endOf("day");

    return (
      current && (current.isBefore(startOfYear) || current.isAfter(endOfYear))
    );
  };

  return (
    <div>
      <div className="tw-flex tw-flex-col tw-mb-3">
        <div className="tw-my-2">
          <label className="tw-font-bold tw-mb-1" htmlFor="performanceYear">
            Performance (Visit) Year
          </label>
          <input
            className=" form-control"
            type="text"
            maxLength={100}
            id="performanceYear"
            value={formData?.performyr}
            disabled
          />
        </div>

        <label className="tw-font-bold tw-mb-1" htmlFor="visitType">
          Visit Type
        </label>
        <MyCustomSelect
          selectOptions={visitTypeOptions}
          myCallBack={onVisitTypeChange}
          selectedValue={visitType}
          errorMessage={pageError?.visitTypeValue}
          id={"visitType"}
        />
        {formData?.visitTypeValue === "other" && (
          <div className="tw-flex tw-flex-col tw-mt-3">
            <label className="tw-font-bold" htmlFor="nppa">
              Other Visit Type
            </label>
            <input
              className=" form-control"
              type="text"
              maxLength={100}
              id="visitTypeOther"
              value={formData?.visitTypeOther}
              onChange={(e) =>
                handleInputChange("visitTypeOther", e.target.value)
              }
              style={{
                border: pageError?.visitTypeOther ? "1px solid red" : "",
              }}
            />
          </div>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold tw-mb-1" htmlFor="visitType">
          Visit Status
        </label>
        <MyCustomSelect
          selectOptions={statusOptions}
          myCallBack={onVisitStatusChange}
          selectedValue={visitStatus}
          errorMessage={pageError?.visitStatusValue}
          id={"visitStatus"}
        />
        {[
          "scheduled",
          "completed",
          "changeOfPCP",
          "dealigned",
          "termedProvider",
          "refused",
        ].includes(formData?.visitStatusValue) && (
          <div className="tw-flex tw-flex-col tw-mt-3">
            <label className="tw-font-bold" htmlFor="hospitalname">
              Date
            </label>
            <DatePicker
              use12Hours
              className="form-control"
              onChange={onVisitStatusDateChange}
              format={dateFormat}
              value={
                formData.visitStatusScheduledDate &&
                dayjs(formData.visitStatusScheduledDate).isValid() &&
                dayjs(formData.visitStatusScheduledDate).format(
                  "YYYY-MM-DD hh:mm A"
                ) !== "1900-01-01 12:00 AM"
                  ? dayjs(formData.visitStatusScheduledDate)
                  : null
              }
              style={{
                border: pageError?.visitStatusScheduledDate
                  ? "1px solid red"
                  : "",
              }}
              id="visitStatusDate"
              disabledDate={disabledDate}
            />
          </div>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold tw-mb-1" htmlFor="visitType">
          Scheduling Details
        </label>
        <MyCustomSelect
          selectOptions={schedulingDetailOptions}
          myCallBack={onSchedulingDetailsChange}
          selectedValue={schedulingDetails}
          id={"schedulingDetails"}
        />
      </div>
      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold tw-mb-1" htmlFor="visitType">
          Scheduling Detail Date
        </label>
        <DatePicker
          use12Hours
          className="form-control"
          onChange={onSchedulingDetailDateChange}
          format={dateFormat}
          value={
            formData.schedulingDetailDate &&
            dayjs(formData.schedulingDetailDate).isValid() &&
            dayjs(formData.schedulingDetailDate).format(
              "YYYY-MM-DD hh:mm A"
            ) !== "1900-01-01 12:00 AM"
              ? dayjs(formData.schedulingDetailDate)
              : null
          }
          id="schedulingDetailDate"
        />
      </div>
      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold tw-mb-1" htmlFor="visitType">
          Rendering Provider
        </label>
        <MyCustomSelect
          selectOptions={provList}
          myCallBack={onRenderingProviderChange}
          selectedValue={renderingProvider}
          id={"renderingProvider"}
        />
      </div>

      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold" htmlFor="providerType">
          Provider Type
        </label>
        <fieldset className="fw-normal">
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="ccmdProvider"
              checked={formData?.providerType === "CCMD Provider"}
              id="ccmdProvider"
              value={"CCMD Provider"}
              onChange={providerTypeChange}
            />
            <label className="form-check-label" htmlFor="ccmdProvider">
              CCMD Provider
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="providerPartner"
              checked={formData?.providerType === "Provider Partner"}
              id="providerPartner"
              value={"Provider Partner"}
              onChange={providerTypeChange}
            />
            <label className="form-check-label" htmlFor="providerPartner">
              Provider Partner
            </label>
          </div>
        </fieldset>
      </div>

      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold" htmlFor="claimType">
          Claim Type
        </label>
        <fieldset className="fw-normal">
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="chartPath"
              checked={formData?.claimType === "ChartPath"}
              id="chartPath"
              value={"ChartPath"}
              onChange={claimTypeChange}
            />
            <label className="form-check-label" htmlFor="chartPath">
              ChartPath
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="paper"
              checked={formData?.claimType === "Paper"}
              id="paper"
              value={"Paper"}
              onChange={claimTypeChange}
            />
            <label className="form-check-label" htmlFor="paper">
              Paper
            </label>
          </div>
        </fieldset>
      </div>

      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold" htmlFor="visitLocation">
          Visit Location
        </label>
        <fieldset className="fw-normal">
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="inPerson"
              checked={formData?.visitLocation === "In Person"}
              id="inPerson"
              value={"In Person"}
              onChange={visitLocationChange}
            />
            <label className="form-check-label" htmlFor="inPerson">
              In Person
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="telehealth"
              checked={formData?.visitLocation === "Telehealth"}
              id="telehealth"
              value={"Telehealth"}
              onChange={visitLocationChange}
            />
            <label className="form-check-label" htmlFor="telehealth">
              Telehealth
            </label>
          </div>
        </fieldset>
      </div>

      <div className="tw-flex tw-flex-col tw-mb-3">
        <label className="tw-font-bold tw-mb-1" htmlFor="visitType">
          Notes
        </label>
        <textarea
          className=" form-control"
          type="text"
          id="notes"
          maxLength={1000}
          value={formData?.notes}
          onChange={(e) => handleInputChange("notes", e.target.value)}
        />
      </div>
      <div className="form-group tw-fixed tw-top-2 tw-right-10">
        <button
          className={
            !disable ? "btn btn-success " : "btn btn-success disabled "
          }
          onClick={onSubmit}
        >
          {editRecord?.guid ? "Update" : "Save"}
        </button>
        <button
          className="btn btn-secondary tw-ml-4"
          onClick={() => callback(false)}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default VisitTrackingForm;
